import { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Stack, Typography, Textarea, Avatar } from "@mui/joy";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import moment from "moment";
import { useNotes } from "../../hooks/query";

interface CommentsProps {
    entityType?: string;
    entityId?: string;
}

export default function Comments({ entityType, entityId }: CommentsProps) {
    const [comment, setComment] = useState("");
    const [comments, setComments] = useState<{ text: string; file?: string; author?: string; date?: string }[]>([]);
    const [file, setFile] = useState<File | null>(null);

    const { useList, useCreate } = useNotes();
    const listSvc = useList();
    const createSvc = useCreate();

    const fetchComments = async () => {
        if (!entityType || !entityId) return;
        try {
            const result = await listSvc.mutateAsync({ EntityType: entityType, EntityId: entityId });
            const mapped = result.map((note: any) => ({
                text: note.Content,
                author: note.CreatedByName || "User",
                date: note.CreatedAt ? moment(note.CreatedAt).fromNow() : "Just now",
            }));
            setComments(mapped);
        } catch (e) {
            console.error("Failed to fetch comments:", e);
        }
    };

    useEffect(() => {
        if (entityType && entityId) {
            fetchComments();
        }
    }, [entityType, entityId]);

    const handleCommentSubmit = async () => {
        if (!comment.trim() && !file) return;

        if (entityType && entityId) {
            try {
                await createSvc.mutateAsync({
                    EntityType: entityType,
                    EntityId: entityId,
                    Content: comment,
                });
                setComment("");
                setFile(null);
                fetchComments();
            } catch (e) {
                console.error("Failed to save comment:", e);
            }
        } else {
            setComments([...comments, { 
                text: comment, 
                file: file?.name,
                author: "You",
                date: "Just now"
            }]);
            setComment("");
            setFile(null);
        }
    };

    return (
        <Box sx={{ mx: "auto" }}>
            {/* Comment Input Section */}
            <Card variant="soft" sx={{ p: 0, position: "relative" }}>
                <CardContent>
                    <Stack>
                        <Textarea
                            sx={{ background: "transparent" }}
                            placeholder="Add inspection notes..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            minRows={4}
                        />
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Button
                                component="label"
                                variant="plain"
                                startDecorator={<AttachFileIcon />}
                                sx={{ position: "absolute", bottom: 12, right: 120 }}
                                size="sm"
                            >
                                Attach Photo
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                />
                            </Button>
                            {/* Submit Button */}
                            <Button
                                onClick={handleCommentSubmit}
                                startDecorator={<SendIcon />}
                                variant="soft"
                                size="sm"
                                sx={{ position: "absolute", bottom: 12, right: 12 }}
                            >
                                Submit
                            </Button>
                        </Stack>
                        {/* Show selected file */}
                        {file && (
                            <Typography level="body-sm" sx={{ color: "gray" }}>
                                {file.name}
                            </Typography>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {/* Comments List */}
            <Stack spacing={1.5} sx={{ mt: 2 }}>
                {comments.map((c, index) => {
                    const initials = c.author
                        ? c.author.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
                        : "?";
                    return (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                gap: 1.5,
                                p: 1.5,
                                borderRadius: "lg",
                                border: "1px solid",
                                borderColor: "divider",
                                bgcolor: "background.surface",
                            }}
                        >
                            <Avatar
                                variant="soft"
                                color="primary"
                                sx={{
                                    width: 32,
                                    height: 32,
                                    fontSize: "xs",
                                    fontWeight: "bold",
                                }}
                            >
                                {initials}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                                    <Typography level="title-sm" sx={{ fontWeight: "bold" }}>
                                        {c.author}
                                    </Typography>
                                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                        {c.date}
                                    </Typography>
                                </Box>
                                <Typography level="body-sm" sx={{ color: "text.primary", whiteSpace: "pre-wrap" }}>
                                    {c.text}
                                </Typography>
                                {c.file && (
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            mt: 1,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            color: "text.secondary",
                                            bgcolor: "background.level1",
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: "md",
                                            border: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    >
                                        📎 {c.file}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}
